/**
 * Data loading.
 *
 * Deliberately small: this panel has six screens and each one loads a list. A
 * query library would be more machinery than the problem has. What matters is
 * that a stale response can never overwrite a fresh one — an operator filtering
 * a queue must not see the previous filter's rows land on top of the new ones.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Re-run the loader, e.g. after a mutation. */
  reload: () => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    let cancelled = false;

    setLoading(true);
    loader()
      .then((result) => {
        if (cancelled || mine !== generation.current) return;
        setData(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled || mine !== generation.current) return;
        setError(cause instanceof Error ? cause.message : 'Something went wrong.');
      })
      .finally(() => {
        if (cancelled || mine !== generation.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
